<modal-dialog #editUserModal [dialogTemplate]="editUserDialogTemplate"></modal-dialog>
<ng-template #editUserDialogTemplate>
  <div class="light-content-panel light-control-host" (keydown.escape)="close()" tabindex="0">
    <div class="modal-header">
      <h2 class="panel-title margin-left">Edit User</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <i class="fa-utility fa-semibold fa-xmark"></i>
      </button>
    </div>
    <form [formGroup]="userForm" (ngSubmit)="onSave()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- User Information Section -->
        <div class="form-section">
          <h4>User Information</h4>
          <div class="flex-row gap-4">
            <div class="form-row flex-1">
              <div class="form-group">
                <label class="form-label">Name</label>
                <!-- TODO: implement update name, ready only and disabled based on isSamlUser() -->
                <input
                  type="text"
                  formControlName="name"
                  class="arpio-input full-width"
                  placeholder="User's full name"
                  readonly
                  disabled
                />
                <div *ngIf="nameErrors" class="input-error-text">
                  {{ nameErrors }}
                </div>
                <div *ngIf="isSamlUser()" class="help-text text-xs">
                  <em>Name is managed by your SSO provider and cannot be changed here.</em>
                </div>
              </div>
            </div>
            <div class="form-row flex-1">
              <div class="form-group flex-1">
                <label class="form-label">Email</label>
                <input
                  type="email"
                  formControlName="email"
                  class="arpio-input full-width"
                  placeholder="user@example.com"
                  readonly
                />
                <div *ngIf="emailErrors" class="input-error-text">
                  {{ emailErrors }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Identity Provider</label>
              <input class="arpio-input full-width" readonly disabled [value]="user?.identityProviderName || 'Arpio'" />
            </div>
          </div>
        </div>
        <hr />

        <!-- Role and Group Assignment Section -->
        <div class="form-section margin-top">
          <h4 *ngIf="accountService.rbacFullEnabled">Role & Group Assignment</h4>
          <div class="role-group-assignment-description">
            <p *ngIf="accountService.rbacFullEnabled" class="heading-sub-p-text">
              Assign roles directly or through groups to define what this user can view or change in Arpio.
              <br />
              <em
                >Roles inherited from groups are indicated by
                <i class="fa-utility-duo fa-semibold fa-users half-margin-left text-xs margin-right"></i>
                SSO roles/groups are indicated by
                <i class="fa-utility-duo fa-semibold fa-id-card half-margin-left text-xs"></i
              ></em>
            </p>
          </div>

          <!-- Tables Section -->
          <div
            class="flex justify-between margin-top"
            [ngStyle]="{ gap: accountService.rbacFullEnabled ? '2rem' : '0' }"
          >
            <!-- Assign Roles Table -->
            <div
              class="table-container"
              [ngStyle]="{ flex: accountService.rbacFullEnabled ? '1' : 'auto' }"
              style="width: 100%"
            >
              <div class="flex-row items-center gap-4 margin-bottom">
                <h3 class="whitespace-nowrap">Assign Roles</h3>
                <div class="filter-container">
                  <input
                    *ngIf="isModalOpen()"
                    pInputText
                    type="text"
                    #rolesFilter
                    (input)="filterRoles($event)"
                    placeholder="Filter roles..."
                    class="arpio-input filter-input"
                  />
                </div>
              </div>
              <p-table
                *ngIf="isModalOpen()"
                #rolesTable
                [value]="availableRoles"
                [globalFilterFields]="['name', 'accessLevel']"
                dataKey="id"
                [scrollable]="true"
                scrollHeight="300px"
                [tableStyle]="{ 'min-width': '20rem' }"
                [styleClass]="'p-datatable-sm'"
                [loading]="loadingRoles"
                [customSort]="true"
                (sortFunction)="customSort($event)"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="areAllRolesSelected()"
                        [indeterminate]="areSomeRolesSelected()"
                        (change)="toggleAllRoles($event)"
                      />
                    </th>
                    <th pSortableColumn="name" class="role-name-column">
                      Role Name <p-sortIcon field="name"></p-sortIcon>
                    </th>
                    <th pSortableColumn="accessLevel">Access Level(s) <p-sortIcon field="accessLevel"></p-sortIcon></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-role>
                  <tr
                    [ngClass]="{
                      'default-role-row': isDefaultAccountRole(role),
                      'group-inherited-role': role.source === 'group' || role.source === 'sso',
                    }"
                  >
                    <td>
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="isRoleSelected(role) || role.source === 'group' || role.source === 'sso'"
                        [disabled]="role.source === 'group' || role.source === 'sso'"
                        (change)="toggleRole(role, $event)"
                      />
                    </td>
                    <td class="role-name-column">
                      <div>
                        {{ role.name }}
                        <i
                          *ngIf="role.source === 'group'"
                          class="fa-utility-duo fa-semibold fa-users text-muted margin-left text-xs"
                          [pTooltip]="getGroupInheritanceTooltip(role.groupNames || [])"
                          tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                          tooltipPosition="top"
                          [autoHide]="false"
                          [hideDelay]="200"
                          [escape]="false"
                          tooltipEvent="hover"
                        >
                        </i>
                        <i
                          *ngIf="role.source === 'sso'"
                          class="fa-utility-duo fa-semibold fa-id-card text-muted margin-left text-xs"
                          pTooltip="Provided by SSO provider"
                          tooltipStyleClass="arp-tooltip"
                          tooltipPosition="top"
                        >
                        </i>
                        <span
                          *ngIf="role.source === 'direct' && role.groupNames && role.groupNames.length > 0"
                          class="text-xs text-muted half-margin-left"
                          [pTooltip]="getGroupInheritanceTooltip(role.groupNames, 'Also inherited from group(s):')"
                          tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                          tooltipPosition="top"
                          [autoHide]="false"
                          [hideDelay]="200"
                          [escape]="false"
                          tooltipEvent="hover"
                        >
                          (+ groups)
                        </span>
                      </div>
                    </td>
                    <td>
                      <div
                        class="access-level-badges"
                        *ngIf="role.accessLevels && role.accessLevels.length > 0; else singleAccessLevel"
                      >
                        <p-tag
                          *ngFor="let accessLevelInfo of role.accessLevels"
                          [severity]="
                            accessLevelInfo.accessLevel.includes('admin')
                              ? 'danger'
                              : accessLevelInfo.accessLevel.includes('read')
                                ? 'warning'
                                : 'info'
                          "
                          class="access-level-badge fit-width"
                          [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                          tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                          tooltipPosition="top"
                          [autoHide]="false"
                          [hideDelay]="200"
                          [escape]="false"
                          tooltipEvent="hover"
                          value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                        >
                        </p-tag>
                      </div>
                      <ng-template #singleAccessLevel>
                        <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                      </ng-template>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="3" class="empty-state-cell">
                      <div class="empty-state-content">
                        <p class="empty-state-message">No roles available</p>
                        <p class="empty-state-submessage">Create roles in Role Management to assign them to users.</p>
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="summary">
                  <div class="flex align-items-center justify-content-between">
                    <span
                      >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                      {{ rolesTable.value?.length }} roles</span
                    >
                  </div>
                </ng-template>
              </p-table>
            </div>

            <!-- Assign Groups Table -->
            <div *ngIf="accountService.rbacFullEnabled" class="table-container" style="flex: 1">
              <div class="flex-row items-center gap-4 margin-bottom">
                <h3 class="whitespace-nowrap">Assign Groups</h3>
                <div class="filter-container">
                  <input
                    *ngIf="isModalOpen()"
                    pInputText
                    type="text"
                    #groupsFilter
                    (input)="filterGroups($event)"
                    placeholder="Filter groups..."
                    class="arpio-input filter-input"
                  />
                </div>
              </div>
              <p-table
                *ngIf="isModalOpen()"
                #groupsTable
                [value]="availableGroups"
                [globalFilterFields]="['name', 'roleCount']"
                dataKey="id"
                [scrollable]="true"
                scrollHeight="300px"
                [tableStyle]="{ 'min-width': '20rem' }"
                [styleClass]="'p-datatable-sm'"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="areAllGroupsSelected()"
                        [indeterminate]="areSomeGroupsSelected()"
                        (change)="toggleAllGroups($event)"
                      />
                    </th>
                    <th pSortableColumn="name">Group Name <p-sortIcon field="name"></p-sortIcon></th>
                    <th pSortableColumn="roleCount">Roles <p-sortIcon field="roleCount"></p-sortIcon></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-group>
                  <tr [ngClass]="{ 'group-inherited-role': group.source === 'sso' }">
                    <td class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="isGroupSelected(group) || group.source === 'sso'"
                        [disabled]="group.source === 'sso'"
                        (change)="toggleGroup(group, $event)"
                      />
                    </td>
                    <td>
                      <div>
                        {{ group.name }}
                        <i
                          *ngIf="group.source === 'sso'"
                          class="fa-utility-duo fa-semibold fa-id-card text-muted margin-left text-xs"
                          pTooltip="Provided by SSO provider"
                          tooltipStyleClass="arp-tooltip"
                          tooltipPosition="top"
                        >
                        </i>
                      </div>
                    </td>
                    <td>{{ group.roleCount }}</td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="3" class="empty-state-cell">
                      <div class="empty-state-content">
                        <p class="empty-state-message">No groups available</p>
                        <p class="empty-state-submessage">Create groups in Group Management to assign them to users.</p>
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="summary">
                  <div class="flex align-items-center justify-content-between">
                    <span
                      >Displaying {{ groupsTable.filteredValue?.length ?? groupsTable.value?.length }} of
                      {{ groupsTable.value?.length }} groups</span
                    >
                  </div>
                </ng-template>
              </p-table>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" (click)="close()">CANCEL</button>
        <button type="submit" class="arpio-button" [disabled]="!canSave || saving">
          <i *ngIf="saving" class="fa-solid fa-spinner fa-spin"></i>
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
